import React, { useState, useRef } from 'react';
import apiClient from '../api/apiClient';
import CodeDisplay from '../components/CodeDisplay';
import ExcelUpload from '../components/ExcelUpload';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const QRCodeGenerator = () => {
  // State for form inputs
  const [qrUrl, setQrUrl] = useState('https://yourdomain.com/api/abc');
  const [qrIds, setQrIds] = useState('1,2');
  const [qrTexts, setQrTexts] = useState('Zone:1,Zone:2');
  const [bulkData, setBulkData] = useState(null);
  const [useBulk, setUseBulk] = useState(false);
  
  // State for response
  const [qrResponse, setQrResponse] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  // Environment info
  const env = import.meta.env.VITE_ENV || 'development';
  const apiUrl = import.meta.env.VITE_API_URL || 'https://localhost:7206';

  // Reference for scrollable container
  const responseContainerRef = useRef(null);

  // Helper: Parse comma-separated values
  const parseCommaList = (str) => {
    if (!str) return [];
    return str.split(',').map(s => s.trim()).filter(Boolean);
  };

  // Generate QR Codes
  const generateQRCodes = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError('');
    setSuccess('');
    setQrResponse([]);
    setProgress({ current: 0, total: 0 });

    try {
      let records = [];
      
      if (useBulk && bulkData) {
        // Use bulk data from Excel
        records = bulkData.map(row => ({
          url: row.url,
          id: row.id,
          text: row.text
        }));
        
        if (records.length === 0) {
          throw new Error('No data found in Excel file.');
        }
        
        if (records.length > 1000) {
          throw new Error(`Too many records (${records.length}). Maximum allowed is 1000.`);
        }
      } else {
        // Use manual input
        const ids = parseCommaList(qrIds);
        const texts = parseCommaList(qrTexts);

        if (ids.length === 0) {
          throw new Error('Please enter at least one ID.');
        }
     //   if (texts.length === 0) {
     //     throw new Error('Please enter at least one text.');
     //   }
     //   if (ids.length !== texts.length) {
      //    throw new Error(`Number of IDs (${ids.length}) and Texts (${texts.length}) must match.`);
     //   }

        records = ids.map((id, index) => ({
          url: qrUrl.trim(),
          id: id,
          text: texts[index]
        }));
      }

      setProgress({ current: 0, total: records.length });

      // Process in batches to handle large volumes
      const batchSize = 50;
      const allResults = [];

      for (let i = 0; i < records.length; i += batchSize) {
        const batch = records.slice(i, i + batchSize);
        const batchIds = batch.map(r => r.id);
        const batchTexts = batch.map(r => r.text);
        const batchUrl = batch[0].url; // Use first URL in batch

        const payload = {
          url: batchUrl,
          ids: batchIds,
          text: batchTexts
        };

        if (env === 'development') {
          console.log(`Processing batch ${Math.floor(i / batchSize) + 1}:`, payload);
        }

        const response = await apiClient.post('/qr/bulk-qr', payload);

        if (!response.data || !Array.isArray(response.data)) {
          throw new Error('Unexpected response format from server.');
        }

        allResults.push(...response.data);
        setProgress({ current: Math.min(i + batchSize, records.length), total: records.length });
      }

      setQrResponse(allResults);
      setSuccess(`✅ Successfully generated ${allResults.length} QR codes!`);
      
      setTimeout(() => {
        if (responseContainerRef.current) {
          responseContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      
    } catch (err) {
      console.error('QR generation error:', err);
      if (err.response) {
        setError(`Server Error (${err.response.status}): ${err.response.data || err.message}`);
      } else if (err.request) {
        setError('No response from server. Please check if the API is running.');
      } else {
        setError(err.message || 'Failed to generate QR codes.');
      }
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  // Clear response
  const clearResponse = () => {
    setQrResponse([]);
    setSuccess('');
    setError('');
  };

  // Sample data for testing
  const loadSampleData = () => {
    setQrUrl('https://yourdomain.com/api/abc');
    setQrIds('1,2,3');
    setQrTexts('Zone:1,Zone:2,Zone:3');
    setUseBulk(false);
    setBulkData(null);
  };

  // Handle bulk data from Excel
  const handleBulkDataLoaded = (data) => {
    if (data) {
      setBulkData(data);
      setUseBulk(true);
      // Clear manual inputs
      setQrIds('');
      setQrTexts('');
    } else {
      setBulkData(null);
      setUseBulk(false);
    }
  };

  // Download all QR codes as ZIP
  const downloadAllQRs = async () => {
    if (qrResponse.length === 0) return;
    
    setDownloading(true);
    setError('');
    
    try {
      const zip = new JSZip();
      const folder = zip.folder('qr-codes');
      
      qrResponse.forEach((item, index) => {
        const id = item.tankId || `qr-${index + 1}`;
        const base64Data = item.qrCodeBase64 || '';
        
        if (base64Data) {
          const binaryData = atob(base64Data);
          const array = new Uint8Array(binaryData.length);
          for (let i = 0; i < binaryData.length; i++) {
            array[i] = binaryData.charCodeAt(i);
          }
          
          folder.file(`qr-${id}.png`, array);
        }
      });
      
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `qr-codes-${new Date().getTime()}.zip`);
      
      setSuccess(`✅ Successfully downloaded ${qrResponse.length} QR codes as ZIP!`);
      
    } catch (err) {
      console.error('Download error:', err);
      setError(`Failed to download QR codes: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  // Download all QR codes as individual files
  const downloadAllQRsIndividual = () => {
    if (qrResponse.length === 0) return;
    
    qrResponse.forEach((item, index) => {
      const id = item.tankId || `qr-${index + 1}`;
      const base64Data = item.qrCodeBase64 || '';
      
      if (base64Data) {
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${base64Data}`;
        link.download = `qr-${id}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });
  };

  // Get records count
  const getRecordCount = () => {
    if (useBulk && bulkData) {
      return bulkData.length;
    }
    const ids = parseCommaList(qrIds);
    return ids.length;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>📌 QR Code Generator</h2>
        <div className="env-badge">
          <span className={`env-dot env-${env}`}></span>
          {env}
          <span className="env-url">{apiUrl}</span>
        </div>
      </div>

      <div className="content-wrapper">
        <div className="form-section">
          <form onSubmit={generateQRCodes}>
            {/* Input Mode Toggle */}
            <div className="input-mode-toggle">
              <button
                type="button"
                className={`btn ${!useBulk ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => {
                  setUseBulk(false);
                  setBulkData(null);
                }}
              >
                📝 Manual Input
              </button>
              <button
                type="button"
                className={`btn ${useBulk ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setUseBulk(true)}
              >
                📤 Bulk Upload
              </button>
            </div>

              {!useBulk ? (
              // Manual Input Mode
              <>
                <div className="form-group">
                  <label htmlFor="qrUrl">Base URL</label>
                  <input
                    id="qrUrl"
                    type="text"
                    value={qrUrl}
                    onChange={(e) => setQrUrl(e.target.value)}
                    placeholder="Enter base URL"
                    className="input-field"
                    required
                  />
                  <small className="helper-text">
                    The base URL will be used for all QR codes
                  </small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="qrIds">IDs (comma separated)</label>
                    <input
                      id="qrIds"
                      type="text"
                      value={qrIds}
                      onChange={(e) => setQrIds(e.target.value)}
                      placeholder="e.g., 1,2,3"
                      className="input-field"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="qrTexts">Texts (comma separated)</label>
                    <input
                      id="qrTexts"
                      type="text"
                      value={qrTexts}
                      onChange={(e) => setQrTexts(e.target.value)}
                      placeholder="e.g., Zone:1,Zone:2,Zone:3"
                      className="input-field"                      
                    />
                  </div>
                </div>

                <div className="record-count">
                  <span className="badge">📊 {getRecordCount()} record(s) to generate</span>
                </div>
              </>
            ) : (
              // Bulk Upload Mode - Pass sampleType='qr'
              <ExcelUpload 
                onDataLoaded={handleBulkDataLoaded}
                maxRows={1000}
                title="📤 Upload Excel for QR Codes"
                description="Upload Excel file with ID, TEXT, and URL columns"
                sampleType="qr"  // <-- This tells it to generate QR sample
              />
            )}

            <div className="button-group">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading || (useBulk && !bulkData)}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Generating {progress.current > 0 ? `${progress.current}/${progress.total}` : '...'}
                  </>
                ) : (
                  `▶ Generate QR Codes${useBulk && bulkData ? ` (${bulkData.length})` : ''}`
                )}
              </button>

              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={loadSampleData}
                disabled={useBulk}
              >
                📋 Load Sample
              </button>

              <button 
                type="button" 
                className="btn btn-danger"
                onClick={clearResponse}
                disabled={qrResponse.length === 0}
              >
                🗑️ Clear
              </button>
            </div>
          </form>

          {/* Progress Bar */}
          {loading && progress.total > 0 && (
            <div className="progress-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
              <span className="progress-text">
                {progress.current} / {progress.total} records processed
              </span>
            </div>
          )}

          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">❌</span>
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{error}</pre>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <span className="alert-icon">✅</span>
              {success}
            </div>
          )}
        </div>

        <div 
          className="response-section" 
          ref={responseContainerRef}
          style={{ maxHeight: '600px', display: 'flex', flexDirection: 'column' }}
        >
          <div className="response-header" style={{ flexShrink: 0 }}>
            <h3>Generated QR Codes</h3>
            <div className="response-header-actions">
              {qrResponse.length > 0 && (
                <span className="code-count">{qrResponse.length} codes</span>
              )}
            </div>
          </div>

          <div 
            className="response-scrollable"
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingRight: '4px',
            }}
          >
            <CodeDisplay 
              items={qrResponse}
              emptyMessage="Enter data and click 'Generate QR Codes' to create QR codes"
            />
          </div>

          {qrResponse.length > 0 && (
            <div className="response-actions" style={{ flexShrink: 0, marginTop: 'auto' }}>
              <div className="download-actions">
                <button 
                  className="btn btn-primary"
                  onClick={downloadAllQRs}
                  disabled={downloading}
                >
                  {downloading ? (
                    <>
                      <span className="spinner"></span>
                      Creating ZIP...
                    </>
                  ) : (
                    '📦 Download All as ZIP'
                  )}
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={downloadAllQRsIndividual}
                  disabled={downloading}
                >
                  ⬇️ Download Individual
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    const summary = qrResponse.map(item => 
                      `ID: ${item.tankId}`
                    ).join('\n');
                    
                    const blob = new Blob([summary], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'qr-codes-summary.txt';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  📝 Download Summary
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;